import { useState, useCallback } from 'react';
import { getFilteredSegments } from '../utils/segmentUtils';
import { Segment, SegmentsByLap } from '../types/segment';

interface SegmentDisplayOptions {
  /** All segments */
  segments: Segment[];
  /** Segments grouped by lap */
  segmentsByLap: SegmentsByLap;
  /** External segments by lap (if provided) */
  externalSegmentsByLap?: SegmentsByLap;
  /** Current lap number */
  currentLapNumber: number;
}

/**
 * Custom hook to manage segment display settings and filtering
 */
const useSegmentDisplay = ({
  segments,
  segmentsByLap,
  externalSegmentsByLap,
  currentLapNumber,
}: SegmentDisplayOptions) => {
  // Track if we need to show all laps or just current lap segments
  const [showAllLaps, setShowAllLaps] = useState<boolean>(false);

  // Use external segments if provided, otherwise use local state
  const effectiveSegmentsByLap = externalSegmentsByLap || segmentsByLap;

  // Highlighted segment (for UI feedback)
  const [currentSegmentDistance, setCurrentSegmentDistance] = useState<number | null>(null);

  /**
   * Toggle between showing all laps or just current lap
   */
  const toggleLapView = useCallback(() => {
    setShowAllLaps(prevState => !prevState);
  }, []);

  /**
   * Highlight a segment distance temporarily (for UI feedback)
   */
  const highlightSegmentDistance = useCallback((distance: number) => {
    setCurrentSegmentDistance(distance);

    // Reset highlight after brief delay
    setTimeout(() => {
      setCurrentSegmentDistance(null);
    }, 300);
  }, []);

  /**
   * Get segments to display based on current view mode
   */
  const getSegmentsToDisplay = useCallback((): Segment[] => {
    return getFilteredSegments({
      showAllLaps,
      allSegments: segments,
      segmentsByLap: effectiveSegmentsByLap,
      currentLapNumber,
    });
  }, [showAllLaps, segments, effectiveSegmentsByLap, currentLapNumber]);

  return {
    showAllLaps,
    currentSegmentDistance,
    effectiveSegmentsByLap,
    toggleLapView,
    highlightSegmentDistance,
    getSegmentsToDisplay,
  };
};

export default useSegmentDisplay;
